import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  IconButton,
  Typography,
  TextField,
  MenuItem,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Card,
  CardContent,
  Avatar
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  History as HistoryIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon,
  Email as EmailIcon,
  PersonAdd as OnboardIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';

const EmployeeTransitionList = () => {
  const navigate = useNavigate();
  const [transitions, setTransitions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    transitionType: '',
    status: '',
    startDate: '',
    endDate: '',
    page: 1,
    limit: 50
  });
  const [totalCount, setTotalCount] = useState(0);
  const [selectedTransition, setSelectedTransition] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [employeeMap, setEmployeeMap] = useState({});

  const userMeta = localStorage.getItem('login_meta');
  const { companyCode, branchId } = userMeta ? JSON.parse(userMeta) : {};

  // Fetch transitions
  const fetchTransitions = async () => {
    setLoading(true);
    try {
      const response = await axios.post('/api/employee-transition/list', filters);
      if (response.data?.success) {
        setTransitions(response.data.data || []);
        setTotalCount(response.data.pagination?.total || 0);
      }
    } catch (error) {
      console.error('Error fetching transitions:', error);
      // Don't show error alert when offline - just keep loading state
      setTransitions([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransitions();
  }, [filters]);

  // Fetch employees for avatars
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await axios.get('/employee/getAllEmployees');
        if (Array.isArray(response.data)) {
          const map = {};
          response.data.forEach(emp => {
            if (emp && emp._id) {
              map[emp._id] = emp;
            }
          });
          setEmployeeMap(map);
        }
      } catch (error) {
        console.error('Error fetching employees for transitions:', error);
      }
    };

    fetchEmployees();
  }, []);

  // Handle filter change
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value, page: 1 }));
  };

  // View transition details
  const handleView = async (id) => {
    try {
      const response = await axios.get(`/api/employee-transition/${id}`);
      if (response.data.success) {
        setSelectedTransition(response.data.data);
        setViewDialogOpen(true);
      }
    } catch (error) {
      console.error('Error fetching transition details:', error);
      Swal.fire('Error', 'Failed to fetch transition details', 'error');
    }
  };

  // Approve transition
  const handleApprove = async (id) => {
    const result = await Swal.fire({
      title: 'Approve Transition?',
      text: 'This action will approve the transition request',
      icon: 'question',
      input: 'textarea',
      inputLabel: 'Comments (optional)',
      inputPlaceholder: 'Enter approval comments...',
      showCancelButton: true,
      confirmButtonText: 'Approve',
      confirmButtonColor: '#4caf50'
    });

    if (result.isConfirmed) {
      try {
        const response = await axios.post(`/api/employee-transition/approve/${id}`, {
          comments: result.value
        });
        
        if (response.data.success) {
          Swal.fire('Success', 'Transition approved successfully', 'success');
          fetchTransitions();
        }
      } catch (error) {
        console.error('Error approving transition:', error);
        Swal.fire('Error', error.response?.data?.message || 'Failed to approve transition', 'error');
      }
    }
  };

  // Reject transition
  const handleReject = async (id) => {
    const result = await Swal.fire({
      title: 'Reject Transition?',
      text: 'Please provide a reason for rejection',
      icon: 'warning',
      input: 'textarea',
      inputLabel: 'Rejection Reason *',
      inputPlaceholder: 'Enter reason for rejection...',
      inputValidator: (value) => {
        if (!value) {
          return 'Rejection reason is required!';
        }
      },
      showCancelButton: true,
      confirmButtonText: 'Reject',
      confirmButtonColor: '#f44336'
    });

    if (result.isConfirmed) {
      try {
        const response = await axios.post(`/api/employee-transition/reject/${id}`, {
          rejectionReason: result.value
        });
        
        if (response.data.success) {
          Swal.fire('Success', 'Transition rejected', 'success');
          fetchTransitions();
        }
      } catch (error) {
        console.error('Error rejecting transition:', error);
        Swal.fire('Error', error.response?.data?.message || 'Failed to reject transition', 'error');
      }
    }
  };

  // Edit transition (navigate to edit form)
  const handleEdit = (id) => {
    navigate(`/userdashboard/hr/employee-transition/edit/${id}`);
  };

  // Delete transition
  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Delete Transition?',
      text: 'This action cannot be undone. Only PENDING transitions can be deleted.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      confirmButtonColor: '#d32f2f',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        const response = await axios.delete(`/api/employee-transition/${id}`);
        
        if (response.data.success) {
          Swal.fire('Deleted!', 'Transition deleted successfully', 'success');
          fetchTransitions();
        }
      } catch (error) {
        console.error('Error deleting transition:', error);
        Swal.fire('Error', error.response?.data?.message || 'Failed to delete transition', 'error');
      }
    }
  };

  // Implement transition
  const handleImplement = async (id) => {
    const result = await Swal.fire({
      title: 'Implement Transition?',
      text: 'This will update the employee record. This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Implement',
      confirmButtonColor: '#2196f3'
    });

    if (result.isConfirmed) {
      try {
        const response = await axios.post(`/api/employee-transition/implement/${id}`);
        
        if (response.data.success) {
          Swal.fire('Success', 'Transition implemented successfully', 'success');
          
          // If Job Offer (Onboarding), redirect to Employee View/Edit to fill details
          if (selectedTransition?.transitionType === 'JOB_OFFER' || 
              transitions.find(t => t._id === id)?.transitionType === 'JOB_OFFER') {
            
            const employee = response.data.data?.employee;
            if (employee?._id) {
              localStorage.setItem("EMP_ID", employee._id);
              navigate("/userdashboard/staff/hrm/manageEmployee/viewEmployee");
            }
          }
          
          fetchTransitions();
        }
      } catch (error) {
        console.error('Error implementing transition:', error);
        Swal.fire('Error', error.response?.data?.message || 'Failed to implement transition', 'error');
      }
    }
  };

  // Send Job Offer Email
  const handleSendEmail = async (id) => {
    const result = await Swal.fire({
      title: 'Send Job Offer Email?',
      text: 'This will send the official job offer email to the candidate.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Send Email',
      confirmButtonColor: '#1976d2'
    });

    if (result.isConfirmed) {
      // Show loading state
      Swal.fire({
        title: 'Sending Email...',
        text: 'Please wait while we send the job offer email.',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      try {
        const response = await axios.post(`/api/employee-transition/send-offer-email/${id}`);
        
        if (response.data.success) {
          Swal.fire('Success', 'Job offer email sent successfully', 'success');
          fetchTransitions();
        }
      } catch (error) {
        console.error('Error sending email:', error);
        Swal.fire('Error', error.response?.data?.message || 'Failed to send email', 'error');
      }
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    const colors = {
      PENDING: 'warning',
      APPROVED: 'info',
      REJECTED: 'error',
      IMPLEMENTED: 'success'
    };
    return colors[status] || 'default';
  };

  // Get transition type icon
  const getTransitionIcon = (type) => {
    switch (type) {
      case 'PROMOTION':
        return <TrendingUpIcon />;
      case 'INCREMENT':
        return <MoneyIcon />;
      case 'JOB_OFFER':
        return <EmailIcon />;
      case 'RESIGNATION':
      case 'TERMINATION':
        return <HistoryIcon color={type === 'TERMINATION' ? 'error' : 'action'} />;
      default:
        return <HistoryIcon />;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">
          Employee Transitions
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/userdashboard/staff/hrm/transitions/create')}
        >
          New Transition
        </Button>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Transitions
              </Typography>
              <Typography variant="h4">{totalCount}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Pending Approval
              </Typography>
              <Typography variant="h4" color="warning.main">
                {transitions?.filter(t => t.status === 'PENDING').length || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Approved
              </Typography>
              <Typography variant="h4" color="info.main">
                {transitions?.filter(t => t.status === 'APPROVED').length || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Implemented
              </Typography>
              <Typography variant="h4" color="success.main">
                {transitions?.filter(t => t.status === 'IMPLEMENTED').length || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              fullWidth
              label="Transition Type"
              value={filters.transitionType}
              onChange={(e) => handleFilterChange('transitionType', e.target.value)}
            >
              <MenuItem value="">All Types</MenuItem>
              <MenuItem value="PROMOTION">Promotion</MenuItem>
              <MenuItem value="TRANSFER">Transfer</MenuItem>
              <MenuItem value="INCREMENT">Increment</MenuItem>
              <MenuItem value="DEMOTION">Demotion</MenuItem>
              <MenuItem value="DESIGNATION_CHANGE">Designation Change</MenuItem>
              <MenuItem value="DEPARTMENT_CHANGE">Department Change</MenuItem>
              <MenuItem value="RESIGNATION">Resignation</MenuItem>
              <MenuItem value="TERMINATION">Termination</MenuItem>
              <MenuItem value="JOB_OFFER">Job Offer</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              fullWidth
              label="Status"
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="PENDING">Pending</MenuItem>
              <MenuItem value="APPROVED">Approved</MenuItem>
              <MenuItem value="REJECTED">Rejected</MenuItem>
              <MenuItem value="IMPLEMENTED">Implemented</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              type="date"
              label="Start Date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              type="date"
              label="End Date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Type</TableCell>
              <TableCell>Employee/Candidate</TableCell>
              <TableCell>Details</TableCell>
              <TableCell>Effective Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : !transitions || transitions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No transitions found
                </TableCell>
              </TableRow>
            ) : (
              transitions.map((transition) => (
                <TableRow key={transition._id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getTransitionIcon(transition.transitionType)}
                      <Typography variant="body2">
                        {transition.transitionType.replace(/_/g, ' ')}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {transition.transitionType === 'JOB_OFFER' ? (
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {transition.jobOffer?.candidateName || 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {transition.jobOffer?.candidateEmail || 'N/A'}
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar 
                          src={employeeMap[transition.employeeId?._id]?.imageUrl}
                          sx={{ 
                            width: 40, 
                            height: 40,
                            bgcolor: 'primary.main'
                          }}
                        >
                          {!employeeMap[transition.employeeId?._id]?.imageUrl && 
                            (transition.employeeId?.staff_name || transition.employeeId?.name)?.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {transition.employeeId?.staff_name || transition.employeeId?.name}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {transition.employeeId?.employeeCode || transition.employeeId?.emp_id}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    {transition.transitionType === 'INCREMENT' && (
                      <Box>
                        <Typography variant="body2">
                          Amount: ₹{transition.incrementAmount?.toLocaleString()}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {transition.incrementPercentage}% increase
                        </Typography>
                      </Box>
                    )}
                    {transition.transitionType === 'PROMOTION' && (
                      <Box>
                        <Typography variant="body2">
                          {transition.previousDesignation?.position || transition.previousDesignation?.name} → {transition.newDesignation?.position || transition.newDesignation?.name}
                        </Typography>
                      </Box>
                    )}
                    {transition.transitionType === 'TRANSFER' && (
                      <Box>
                        {transition.previousDepartment && transition.newDepartment && (
                          <Typography variant="body2">
                            Dept: {transition.previousDepartment?.departmentName || 'N/A'} → {transition.newDepartment?.departmentName || 'N/A'}
                          </Typography>
                        )}
                        {/* Only show Branch Transfer if branches are different */}
                        {transition.previousBranch && transition.newBranch && 
                         transition.previousBranch._id !== transition.newBranch._id && (
                          <Typography variant="caption" color="textSecondary" display="block">
                            {transition.previousBranch?.storeName || transition.previousBranch?.storeCode || 'Previous'} → {transition.newBranch?.storeName || transition.newBranch?.storeCode || 'New'}
                          </Typography>
                        )}
                      </Box>
                    )}
                    {transition.transitionType === 'DESIGNATION_CHANGE' && (
                      <Box>
                        <Typography variant="body2">
                          {transition.previousDesignation?.position || 'Previous'} → {transition.newDesignation?.position || 'New'}
                        </Typography>
                      </Box>
                    )}
                    {transition.transitionType === 'DEPARTMENT_CHANGE' && (
                      <Box>
                        <Typography variant="body2">
                          {transition.previousDepartment?.departmentName || 'Previous'} → {transition.newDepartment?.departmentName || 'New'}
                        </Typography>
                      </Box>
                    )}
                    {transition.transitionType === 'DEMOTION' && (
                      <Box>
                        <Typography variant="body2" color="error">
                          {transition.previousDesignation?.position || 'Previous'} → {transition.newDesignation?.position || 'New'}
                        </Typography>
                      </Box>
                    )}
                    {transition.transitionType === 'JOB_OFFER' && (
                      <Box>
                        <Typography variant="body2">
                          Position: {transition.jobOffer?.jobTitle || 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Salary: ₹{transition.jobOffer?.offeredSalary?.toLocaleString()}
                        </Typography>
                      </Box>
                    )}
                    {['RESIGNATION', 'TERMINATION'].includes(transition.transitionType) && (
                      <Box>
                        <Typography variant="body2">
                          LWD: {transition.resignation?.lastWorkingDate ? new Date(transition.resignation.lastWorkingDate).toLocaleDateString() : 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Notice: {transition.resignation?.noticePeriodDays} Days
                        </Typography>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    {new Date(transition.effectiveDate).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={transition.status}
                      color={getStatusColor(transition.status)}
                      size="small"
                    />
                    {transition.jobOffer?.emailSent && (
                      <Chip
                        label="Email Sent"
                        color="primary"
                        size="small"
                        icon={<EmailIcon sx={{ fontSize: '14px !important' }} />}
                        sx={{ ml: 1, height: 24 }}
                      />
                    )}
                    {transition.jobOffer?.status === 'ACCEPTED' && (
                      <Chip
                        label="Offer Accepted"
                        color="success"
                        size="small"
                        sx={{ ml: 1, height: 24 }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => handleView(transition._id)}
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      
                      {transition.status === 'PENDING' && (
                        <>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleEdit(transition._id)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDelete(transition._id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Approve">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleApprove(transition._id)}
                            >
                              <ApproveIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reject">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleReject(transition._id)}
                            >
                              <RejectIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      
                      {transition.status === 'APPROVED' && (
                        <Tooltip title={transition.transitionType === 'JOB_OFFER' ? "Create Employee Record" : "Implement Change"}>
                          <Button
                            size="small"
                            variant="contained"
                            color={transition.transitionType === 'JOB_OFFER' ? 'success' : 'primary'}
                            startIcon={transition.transitionType === 'JOB_OFFER' ? <OnboardIcon /> : undefined}
                            onClick={() => handleImplement(transition._id)}
                          >
                            {transition.transitionType === 'JOB_OFFER' ? 'Onboard Candidate' : 'Implement'}
                          </Button>
                        </Tooltip>
                      )}

                      {/* Send Email Button for Job Offers */}
                      {transition.transitionType === 'JOB_OFFER' && 
                       !transition.jobOffer?.emailSent && (
                        <Tooltip title="Send Offer Email">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleSendEmail(transition._id)}
                          >
                            <EmailIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* View Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Transition Details
        </DialogTitle>
        <DialogContent>
          {selectedTransition && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Employee
                  </Typography>
                  <Typography variant="body1">
                    {selectedTransition.employeeId?.staff_name || selectedTransition.employeeId?.name}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Type
                  </Typography>
                  <Typography variant="body1">
                    {selectedTransition.transitionType}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Effective Date
                  </Typography>
                  <Typography variant="body1">
                    {new Date(selectedTransition.effectiveDate).toLocaleDateString()}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Status
                  </Typography>
                  <Chip
                    label={selectedTransition.status}
                    color={getStatusColor(selectedTransition.status)}
                    size="small"
                  />
                </Grid>
                
                {/* Transfer Details */}
                {selectedTransition.transitionType === 'TRANSFER' && (
                  <>
                    <Grid item xs={12}>
                      <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                        From (Current)
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Department
                      </Typography>
                      <Typography variant="body1">
                        {selectedTransition.previousDepartment?.departmentName || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Branch/Location
                      </Typography>
                      <Typography variant="body1">
                        {selectedTransition.previousBranch?.storeName || selectedTransition.previousBranch?.storeCode || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                        To (New)
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Department
                      </Typography>
                      <Typography variant="body1">
                        {selectedTransition.newDepartment?.departmentName || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Branch/Location
                      </Typography>
                      <Typography variant="body1">
                        {selectedTransition.newBranch?.storeName || selectedTransition.newBranch?.storeCode || 'N/A'}
                      </Typography>
                    </Grid>
                  </>
                )}

                {/* Job Offer Details */}
                {selectedTransition.transitionType === 'JOB_OFFER' && (
                  <>
                    <Grid item xs={12}>
                      <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                        Offer Details
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Job Title
                      </Typography>
                      <Typography variant="body1">
                        {selectedTransition.jobOffer?.jobTitle}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Offered Salary
                      </Typography>
                      <Typography variant="body1">
                        ₹{selectedTransition.jobOffer?.offeredSalary?.toLocaleString()}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Joining Date
                      </Typography>
                      <Typography variant="body1">
                        {selectedTransition.jobOffer?.joiningDate ? new Date(selectedTransition.jobOffer.joiningDate).toLocaleDateString() : 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Offer Expiry
                      </Typography>
                      <Typography variant="body1">
                        {selectedTransition.jobOffer?.expiryDate ? new Date(selectedTransition.jobOffer.expiryDate).toLocaleDateString() : 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Email Sent
                      </Typography>
                      <Typography variant="body1">
                        {selectedTransition.jobOffer?.emailSent ? `Yes (${new Date(selectedTransition.jobOffer.emailSentAt).toLocaleDateString()})` : 'No'}
                      </Typography>
                    </Grid>
                    {selectedTransition.jobOffer?.offerLetterUrl && (
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Offer Letter
                        </Typography>
                        <Typography variant="body1" component="a" href={selectedTransition.jobOffer.offerLetterUrl} target="_blank">
                          View Document
                        </Typography>
                      </Grid>
                    )}
                  </>
                )}

                {/* Resignation/Termination Details */}
                {['RESIGNATION', 'TERMINATION'].includes(selectedTransition.transitionType) && (
                  <>
                    <Grid item xs={12}>
                      <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                        {selectedTransition.transitionType === 'RESIGNATION' ? 'Resignation Details' : 'Termination Details'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Notice Period
                      </Typography>
                      <Typography variant="body1">
                        {selectedTransition.resignation?.noticePeriodDays} Days
                        {selectedTransition.resignation?.isNoticePeriodWaived && ' (Waived)'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Last Working Date
                      </Typography>
                      <Typography variant="body1">
                        {selectedTransition.resignation?.lastWorkingDate ? new Date(selectedTransition.resignation.lastWorkingDate).toLocaleDateString() : 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Reason
                      </Typography>
                      <Typography variant="body1">
                        {selectedTransition.resignation?.reason}
                      </Typography>
                    </Grid>
                    {selectedTransition.resignation?.exitInterviewNotes && (
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Exit Interview Notes
                        </Typography>
                        <Typography variant="body1">
                          {selectedTransition.resignation?.exitInterviewNotes}
                        </Typography>
                      </Grid>
                    )}
                  </>
                )}

                {/* Increment Details - only for INCREMENT transitions with positive amount */}
                {selectedTransition.transitionType === 'INCREMENT' &&
                 selectedTransition.incrementAmount > 0 && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Increment Amount
                      </Typography>
                      <Typography variant="body1">
                        ₹{selectedTransition.incrementAmount?.toLocaleString()}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Increment Percentage
                      </Typography>
                      <Typography variant="body1">
                        {selectedTransition.incrementPercentage}%
                      </Typography>
                    </Grid>
                  </>
                )}
                
                {selectedTransition.notes && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Notes
                    </Typography>
                    <Typography variant="body1">
                      {selectedTransition.notes}
                    </Typography>
                  </Grid>
                )}

                {/* Approval / other comments history */}
                {Array.isArray(selectedTransition.comments) && selectedTransition.comments.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 0.5 }}>
                      Comments
                    </Typography>
                    {selectedTransition.comments.map((c, idx) => (
                      <Box key={idx} sx={{ mb: 0.5 }}>
                        <Typography variant="caption" color="textSecondary">
                          {(c.commentBy && c.commentBy.name) ? c.commentBy.name : 'System'}
                          {c.commentedAt && ` | ${new Date(c.commentedAt).toLocaleString()}`}
                        </Typography>
                        <Typography variant="body2">
                          {c.comment}
                        </Typography>
                      </Box>
                    ))}
                  </Grid>
                )}

                {selectedTransition.rejectionReason && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="error">
                      Rejection Reason
                    </Typography>
                    <Typography variant="body1">
                      {selectedTransition.rejectionReason}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmployeeTransitionList;
